import React, { FC } from 'react';

import { Box, Chip, LinearProgress, Stack, Typography } from 'components';

import { useTranslation } from 'third-party';

import {
  UNLIMITED_BANK_ACCOUNTS,
  USAGE_FULL_PCT,
  USAGE_WARNING_THRESHOLD_PCT,
} from 'constants/common';

import { BillingPlan } from 'modules/billing';
import { OrgUsage } from 'modules/superAdmin';

type Props = {
  usage: OrgUsage;
  plan: BillingPlan | null;
  bankAccountCount: number;
};

export const UsagePanel: FC<Props> = ({ usage, plan, bankAccountCount }) => {
  const { t } = useTranslation();

  const monthlyPct =
    usage.monthlyIncluded > 0
      ? Math.min(
          USAGE_FULL_PCT,
          (usage.monthlyUsed / usage.monthlyIncluded) * USAGE_FULL_PCT,
        )
      : USAGE_FULL_PCT;

  const isMonthlyExhausted = usage.monthlyRemaining <= 0;
  const isOutOfChecks = isMonthlyExhausted && usage.packRemaining <= 0;

  const progressColor = () => {
    if (isMonthlyExhausted) return 'error';
    if (monthlyPct >= USAGE_WARNING_THRESHOLD_PCT) return 'warning';
    return 'primary';
  };

  const bankLimitLabel =
    plan?.maxBankAccounts === UNLIMITED_BANK_ACCOUNTS
      ? t('billing.usage.banksUnlimited', { count: bankAccountCount })
      : t('billing.usage.banksOfLimit', {
          count: bankAccountCount,
          limit: plan?.maxBankAccounts ?? 0,
        });

  if (usage.isUnlimited) {
    return (
      <Box
        sx={{
          p: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>
          {t('billing.usage.unlimitedValue')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {t('billing.usage.unlimitedHint', { count: usage.totalUsed })}
        </Typography>
      </Box>
    );
  }

  // A super admin credit grant overrides the plan, so there is no monthly split to show.
  if (!usage.planCode) {
    return (
      <Box
        sx={{
          p: 2.5,
          border: '1px solid',
          borderColor: usage.remaining <= 0 ? 'error.main' : 'divider',
          borderRadius: 1,
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography
            sx={{
              fontSize: '2rem',
              fontWeight: 700,
              lineHeight: 1,
              color: usage.remaining <= 0 ? 'error.main' : 'text.primary',
            }}
          >
            {usage.remaining}
          </Typography>
          <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
            / {usage.limit}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {t('billing.usage.grantHint')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: isOutOfChecks ? 'error.main' : 'divider',
        borderRadius: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        mb={1.5}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            {t('billing.usage.monthHeader')}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 700,
                lineHeight: 1,
                color: isMonthlyExhausted ? 'error.main' : 'text.primary',
              }}
            >
              {usage.monthlyUsed}
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
              / {usage.monthlyIncluded}
            </Typography>
          </Stack>
        </Box>

        <Box textAlign="right">
          <Typography variant="body2" color="text.secondary">
            {t('billing.usage.packHeader')}
          </Typography>
          <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>
            {usage.packRemaining}
          </Typography>
        </Box>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={monthlyPct}
        color={progressColor()}
        sx={{ height: 8, borderRadius: 4 }}
      />

      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        spacing={1}
        mt={1.5}
        useFlexGap
      >
        <Typography variant="body2" color="text.secondary">
          {t('billing.usage.totalRemaining', { count: usage.remaining })}
        </Typography>
        <Chip size="small" label={bankLimitLabel} />
        {isOutOfChecks && (
          <Chip
            size="small"
            color="error"
            label={t('billing.usage.outOfChecks')}
          />
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" mt={1}>
        {t('billing.usage.drawOrder')}
      </Typography>
    </Box>
  );
};
